import React, {useEffect, useState} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem, Select,
    TextField
} from "@mui/material";
import {
    datetime_format,
    GearCategory,
    GearItem,
    useAddGearWeight,
    useGearItemWeights,
    useUpdateGearItem
} from "../../api";
import dayjs from "dayjs";

interface EditGearItemModalProps {
    existing_categories: Record<number, GearCategory>;
    item_to_edit: GearItem;

    onClose(): void;
    onSave(item: GearItem): void;
}

export function EditGearItemModal(props: EditGearItemModalProps) {
    const [gear_item, set_gear_item] = useState(props.item_to_edit);
    const update_gear_item = useUpdateGearItem()[1]
    const add_weight_measurement = useAddGearWeight()[1];
    const gear_weights = useGearItemWeights(gear_item.id)[0];
    const [current_weight, set_current_weight] = useState('0');
    const [is_weight_dirty, set_is_weight_dirty] = useState(false);

    useEffect(() => {
        if (gear_weights && gear_weights.length > 0) {
            let most_recent_weight = gear_weights[0];
            let most_recent_weight_date = dayjs(most_recent_weight.datetime, datetime_format);
            for (const weight of gear_weights) {
                const entry_date = dayjs(weight.datetime, datetime_format);
                if (most_recent_weight_date.isBefore(entry_date)) {
                    most_recent_weight = weight;
                    most_recent_weight_date = entry_date
                }
            }
            set_current_weight(most_recent_weight.weight.toString());
        }
    }, [gear_weights]);

    const on_confirm_item = () => {
        update_gear_item(gear_item, () => {
            props.onClose();
        });
        if (is_weight_dirty) {
            add_weight_measurement({
                gear_id: gear_item.id,
                weight: Number(current_weight),
            }, () => {});
        }
        props.onSave(gear_item);
    }

    const is_item_valid = gear_item
        && gear_item.make.length > 0
        && gear_item.model.length > 0
        && gear_item.category_id >= 0
        && !isNaN(Number(current_weight))
        && current_weight.length > 0;

    return <div>
        <Dialog open={true}
                onClose={props.onClose}>
            <DialogTitle>{`Edit ${gear_item?.make} - ${gear_item?.model}`}</DialogTitle>
            <DialogContent>
                <TextField autoFocus
                           required
                           margin="dense"
                           id="make"
                           label="Make"
                           fullWidth
                           variant="standard"
                           value={gear_item?.make}
                           onChange={(e) => set_gear_item({...gear_item, make: e.target.value})}/>
                <TextField autoFocus
                           required
                           margin="dense"
                           id="model"
                           label="Model"
                           fullWidth
                           variant="standard"
                           value={gear_item.model}
                           onChange={(e) => set_gear_item({...gear_item, model: e.target.value})}/>
                <TextField margin="dense"
                           id="description"
                           label="Description"
                           multiline
                           fullWidth
                           variant="standard"
                           value={gear_item.description}
                           onChange={(e) => set_gear_item({...gear_item, description: e.target.value})}/>
                <TextField required
                           margin="dense"
                           id="weight"
                           label="Weight"
                           fullWidth
                           variant="standard"
                           type="number"
                           value={current_weight}
                           error={isNaN(Number(current_weight)) || current_weight.length === 0}
                           onChange={(e) => {
                               set_current_weight(e.target.value);
                               set_is_weight_dirty(true);
                           }}/>
                <Select value={gear_item.category_id}
                        onChange={(e) => set_gear_item({...gear_item, category_id: Number(e.target.value)})}
                        fullWidth>
                    <MenuItem value={-1}>Unset</MenuItem>
                    {Object.values(props.existing_categories).map((category, i) => (
                        <MenuItem key={i} value={category.id}>{category.name}</MenuItem>
                    ))}
                </Select>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Cancel</Button>
                <Button disabled={!is_item_valid}
                        onClick={() => on_confirm_item()}>
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    </div>
}